import { isRecordingExpiryLogoutSuppressionKilled } from '@/app-shell/flags/recording-expiry-logout-suppression-flag';
import { isTokenless401LogoutGuardKilled } from '@/app-shell/flags/tokenless-401-logout-guard-flag';
import { isWifiSyncActiveAtom } from '@/atoms/chronicle/bulk-sync';
import { track } from '@/lib/analytics/track';
import { isLogoutInFlight } from '@/lib/authInFlight';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import type { ForcedLogoutReason } from '@/models/analytics/features/auth';
import { store } from '@/store';
import { isTauri } from '@/tauri/utils';
import {
  AUTH_PROVIDER_COOKIE,
  authProviderCookieDeleteIdentity,
  hostOnlyAuthProviderCookieDeleteIdentity,
} from '@/utils/auth/authProviderCookie';
import type { AuthProvider } from '@/utils/auth/persistedAuthProvider';
import { isRecoveryEligible } from '@/utils/http/auth401RecoveryEligibility';
import {
  recordAuth401RecoveryRoute,
  type Auth401RecoveryRouteReason,
} from '@/utils/http/auth401RecoveryTelemetry';
import { type AuthRetriedConfig } from '@/utils/http/authRetry';
import { isDesktopOfflineNetworkError } from '@/utils/http/desktopOfflineNetwork';
import { redactHeadersInPlace } from '@/utils/http/redactHeaders';
import { logger } from '@/utils/logger/logger';
import { sanitizeError } from '@/utils/logger/sanitizeError';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { isAuthRetryRevisionStaleError } from '@heidi/api/errors';
import { toast } from '@heidi/design-system/toast';
import {
  CONTENT_SECURITY_ERROR,
  TIMELINE_STREAM_ERROR,
  isUserRefBannedErrorResponse,
} from '@heidi/evidence/data';
import { getCurrentWindowLabel, isAuxiliaryWindow } from '@heidi/plugin-core';
import { GET_WORKSPACES } from '@heidi/workspace/contracts';
import type { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { deleteCookie } from 'cookies-next/client';
import i18next from 'i18next';
import { getDefaultStore } from 'jotai';
import { throttle } from 'lodash-es';

/**
 * Returns true when WiFi bulk sync is active on the desktop app.
 * During sync, macOS routes all traffic through the device's WiFi hotspot
 * (which has no internet), so network errors are expected and should be suppressed.
 */
function isWifiSyncNetworkError(): boolean {
  if (!isTauri()) return false;
  try {
    return getDefaultStore().get(isWifiSyncActiveAtom);
  } catch {
    return false;
  }
}

/**
 * Machine-readable `detail.type` of ml-scribe's workspace-membership 403
 * (apis/utils/workspace_errors.py). Stable contract; `detail.msg` may change.
 */
export const NOT_A_WORKSPACE_MEMBER_TYPE = 'NotAWorkspaceMember';

let lastLogoutTriggeredAt = 0;
const LOGOUT_COOLDOWN_MS = 30_000;

export const resetLogoutCooldown = () => {
  lastLogoutTriggeredAt = 0;
};

type ForcedLogoutTrigger = {
  reason: ForcedLogoutReason;
  /**
   * When `false`, skip the "no Redux token, already logged out" short-circuit.
   * Use for server-authoritative `session_invalid` signals where stale state
   * (cookies, IndexedDB, persisted models, Posthog/Amplitude identity) needs
   * cleaning even though Redux happens to be empty.
   */
  requireToken: boolean;
};

/**
 * While a desktop recording timer is running, skip forced logout on axios 401 /
 * session-invalid so mid-consult auth failures do not clear the Auth0 session.
 */
function shouldSuppressExpiryLogoutDuringDesktopRecording(): boolean {
  if (!isTauri()) return false;
  if (isRecordingExpiryLogoutSuppressionKilled()) return false;
  try {
    return Boolean(store.getState().timerModel?.isRunning);
  } catch {
    return false;
  }
}

// Shared logout primitive. Called from multiple sources (per-client HTTP
// interceptors via `createHttpRejectedErrorHandler`, and the reactive
// session-invalid effect in `useAuth0Session`), so its logs use a generic
// `[auth-logout]` prefix rather than a particular client name. Per-client
// context lives on the call site's own log line.
export const debouncedExpiryLogout = throttle(
  (trigger: ForcedLogoutTrigger = { reason: 'request_401', requireToken: true }) => {
    // E2E runs share a small storage-state pool. A 401 here would dispatch
    // auth.logout() → /api/auth0/logout, terminating the Auth0 SSO session
    // for the worker's account and poisoning every subsequent test on that
    // worker.
    //
    // The literal NEXT_PUBLIC_ENVIRONMENT comparison (not `getEnvironment()`)
    // is the canonical build-time DCE pattern used for in-app dev-tools
    // gating in src/pages/_app.tsx. Next.js inlines the env var, Terser folds
    // `'production' !== 'production'` to `false`, and webpack tree-shakes the
    // whole conditional out of production bundles — so window.__IS_E2E__ is
    // physically unreachable in prod regardless of what page-level code sets
    // it (XSS, malicious extension, DevTools console).
    // `NEXT_PUBLIC_TAURI_E2E` covers the desktop E2E build's first-boot
    // document, where the Playwright init script that sets
    // `window.__IS_E2E__` hasn't applied yet — a 401 during that window
    // would call auth_logout, which revokes the refresh token server-side
    // and poisons every later test cloned from the same master profile.
    if (
      process.env.NEXT_PUBLIC_ENVIRONMENT !== 'production' &&
      ((typeof window !== 'undefined' && window.__IS_E2E__) ||
        process.env.NEXT_PUBLIC_TAURI_E2E === 'true')
    ) {
      logger.info('[auth-logout] Skipping session-expiry logout - E2E mode');
      return;
    }

    // An auxiliary window has no refresh scheduler; it mirrors the main window's
    // token through `useAuthTokenStorageSync`, so its 401 says nothing about the
    // session. Logging out from there broadcast the logout to the main window and
    // walked the auxiliary webview onto `/desktop-logout` and `/scribe`. The main
    // window owns session expiry, and its logout reaches this window the same way.
    if (isAuxiliaryWindow(getCurrentWindowLabel())) {
      logger.info('[auth-logout] Skipping session-expiry logout - auxiliary window', {
        reason: trigger.reason,
      });
      return;
    }

    if (shouldSuppressExpiryLogoutDuringDesktopRecording()) {
      logger.info(
        '[auth-logout] Skipping session-expiry logout - desktop recording active',
        { reason: trigger.reason }
      );
      return;
    }

    const token = store.select.auth.getToken(store.getState());
    if (trigger.requireToken && !token) {
      logger.info('[auth-logout] Skipping session-expiry logout - already logged out');
      return;
    }

    const isLogoutInProgress = isLogoutInFlight();
    if (isLogoutInProgress) {
      logger.info(
        '[auth-logout] Skipping session-expiry logout - intentional logout in flight'
      );
      return;
    }

    const now = Date.now();
    if (now - lastLogoutTriggeredAt < LOGOUT_COOLDOWN_MS) {
      logger.info('[auth-logout] Skipping session-expiry logout - still within cooldown');
      return;
    }
    lastLogoutTriggeredAt = now;

    logger.info('[auth-logout] Session-expiry logout triggered', {
      reason: trigger.reason,
    });

    track({
      event_name: TrackingEventName.ForcedLogout,
      props: { reason: trigger.reason },
    });

    // `force: true` so cleanup (cookies, IndexedDB, persisted models,
    // analytics identity) still runs when a server-authoritative
    // session_invalid arrives while the user is on a public route.
    void store.dispatch.auth.logout({ force: true, audioWipeReason: 'session-expiry' });

    if (!isEmbedOrPublicChat()) {
      toast({
        tone: 'destructive',
        title: i18next.t('desktopAuth.sessionTimeout.title'),
        duration: 3000,
      });
    }
  },
  1000
);

const isEmbedOrPublicChat = (): boolean => {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname;
  const isInIframe = window.parent !== window;
  return isInIframe || path.startsWith('/chat');
};

/**
 * Deletes both `heidi-auth-provider` cookie identities — the current
 * domain-scoped one AND the historical host-only one — before firing a
 * forced logout so the loop-guard survives in third-party iframe
 * contexts (EHR embeds). Both deletes carry `sameSite:'none',
 * `secure:true` matching the set attrs; omitting them causes Chrome to
 * reject the expired-cookie write there, leaving the cookie alive and
 * re-entering the Auth0 branch on the next page load.
 */
function deleteAuthProviderCookiesBeforeLogout(): void {
  deleteCookie(AUTH_PROVIDER_COOKIE, hostOnlyAuthProviderCookieDeleteIdentity);
  deleteCookie(AUTH_PROVIDER_COOKIE, authProviderCookieDeleteIdentity);
}

function fireRequest401Logout(): void {
  deleteAuthProviderCookiesBeforeLogout();
  debouncedExpiryLogout({ reason: 'request_401', requireToken: false });
}

/**
 * True when a captured {provider, authRevision} still matches the current
 * auth identity. Only meaningful for a non-empty `provider` (a context that
 * WAS recovery-eligible at dispatch) — callers must gate on that themselves.
 *
 * Reads through the ONE shared auth-identity snapshot
 * (`@/lib/auth/authIdentitySnapshot`) — never Redux `authProvider` directly.
 * On web the effective provider is cookie-first and can diverge from Redux
 * during a migration/sync window; comparing against raw Redux here (as this
 * used to) could reject a request that's actually still current, or miss an
 * effective-provider change Redux hasn't caught up to yet — the same
 * snapshot the auth-cache's own commit/acceptance gates and request-context
 * capture already use. Lazy import: `authIdentitySnapshot.ts` reaches
 * `@/store`, which transitively reaches back to this module through the
 * HTTP-client wiring (`store.ts` → models → `scribeHTTP.ts` →
 * `scribeHttpErrorHandler.ts`) — a static import here would cycle.
 */
export async function isCapturedContextCurrent(context: {
  provider: string;
  authRevision: number;
}): Promise<boolean> {
  try {
    const { getCurrentAuthIdentitySnapshot } =
      await import('@/lib/auth/authIdentitySnapshot');
    const live = getCurrentAuthIdentitySnapshot();
    return (
      live.provider === context.provider && live.authRevision === context.authRevision
    );
  } catch (err) {
    // Fail CLOSED: every caller treats a `false` return the same way it
    // treats a live mismatch — reject the original 401 non-destructively,
    // no retry, no legacy delegation, no logout. An import failure here
    // gives no evidence the captured context is still current, so "current"
    // is never the safe assumption.
    logger.warn(
      '[auth-retry] isCapturedContextCurrent - identity snapshot import failed, treating as not current',
      {
        error: err instanceof Error ? err.message : String(err),
      }
    );
    return false;
  }
}

/**
 * Recognises the retry-pin's non-destructive cancellation sentinel (thrown
 * from a request interceptor when a pinned retry has no current context to
 * validate against) and rejects it directly. A plain thrown Error re-enters
 * response handling with no `.response`/`.config`, so every implementation
 * that could see one must check for the sentinel BEFORE any status/config-
 * dependent branch — otherwise it falls into generic "request failed"
 * logging or, worse, a legacy 401 branch that reads a status that was never
 * there. Returns `null` when `error` isn't the sentinel — the caller
 * proceeds with its normal handling.
 */
export function rejectIfAuthRetryStale(error: AxiosError): Promise<never> | null {
  if (!isAuthRetryRevisionStaleError(error)) return null;
  // Centralized log point for the sentinel regardless of which request
  // interceptor threw it (the shared client factory's retry pin, or
  // globalHTTP's own) — packages/api cannot import the application logger,
  // so this is the earliest place in application code the sentinel is seen.
  logger.info('[auth-retry] non-destructive cancellation - retry pin rejected');
  return Promise.reject(error);
}

// Known EHR/agent paths where 504/503 indicates an Integration Agent issue (INT-4858).
// Kept as an explicit allowlist — avoid substring matches on /integrations/ which
// would catch unrelated routes (/integrations/billing, OAuth callbacks, etc.).
const EHR_AGENT_PATH_PREFIXES = [
  '/ehr/',
  '/integrations/schedule',
  '/integrations/links/',
];

function isEhrIntegrationPath(url: string | undefined): boolean {
  if (!url) return false;
  return EHR_AGENT_PATH_PREFIXES.some((prefix) => url.includes(prefix));
}

// TODO(INT-4901): Wire these strings into the i18n pipeline once the
// Integration Agent feature ships. The global HTTP handler is non-React
// and cannot use react-intl hooks, so plain strings are used for now.
const AGENT_TIMEOUT_MESSAGE = 'EHR connection timed out — please try again';
const AGENT_OFFLINE_MESSAGE =
  "Your Integration Agent is offline. Check it's running at the clinic.";

function wasRequestDispatchedWithoutBearer(
  config: InternalAxiosRequestConfig | undefined
): boolean {
  return config?.__authHadBearer === false;
}

/**
 * Build an axios response error-interceptor tagged with the client's name.
 *
 * `clientName` becomes the log prefix on every line this handler emits —
 * e.g. `[scribeHTTP] 401 received…` for the ml-scribe instance, or
 * `[clinicalCodingHTTP] 401 received…` for the clinical-coding instance.
 * Without this, every consumer of the shared handler logs as `[scribeHTTP]`
 * and 401-from-CC vs 401-from-ml-scribe is indistinguishable at triage.
 *
 * The shared `debouncedExpiryLogout` primitive logs under `[auth-logout]`
 * regardless of which client triggered it, because once the call reaches
 * the throttled logout path it's no longer per-client business.
 */
export function createHttpRejectedErrorHandler(clientName: string) {
  const prefix = `[${clientName}]`;
  return (error: AxiosError) => {
    // A plain thrown Error, not a real request failure — recognise and
    // reject it before any status/config-dependent branch below, which
    // would otherwise treat it as an opaque non-401 and log it as one.
    const staleRetryRejection = rejectIfAuthRetryStale(error);
    if (staleRetryRejection) return staleRetryRejection;

    redactHeadersInPlace(error.config?.headers);
    redactHeadersInPlace(error.response?.config?.headers);

    // The Kinde→Auth0 migration revoke carries the old Kinde token in the
    // `explicitKindeToken` config field — a plain field the header redaction
    // above does not cover. Neutralise it before the error can reach any
    // logger/telemetry (e.g. the revoke's own `.catch` → Datadog). Keep a
    // truthy sentinel so the 401 carve-out below still identifies the revoke.
    if (error.config?.explicitKindeToken) {
      error.config.explicitKindeToken = '[REDACTED]';
    }

    // During WiFi bulk sync, macOS routes traffic through the device's
    // internet-less hotspot. Suppress network errors to avoid log noise
    // and unnecessary 401-triggered logouts.
    if (
      !error.response &&
      (isWifiSyncNetworkError() || isDesktopOfflineNetworkError(error))
    ) {
      return Promise.reject(error);
    }

    // 504 on EHR paths: the Integration Agent timed out waiting for the EHR.
    if (error?.response?.status === 504 && isEhrIntegrationPath(error.config?.url)) {
      toast({ tone: 'destructive', title: AGENT_TIMEOUT_MESSAGE });
      return Promise.reject(error);
    }

    // 503 on EHR paths: no active Integration Agent found for the clinic.
    if (error?.response?.status === 503 && isEhrIntegrationPath(error.config?.url)) {
      toast({ tone: 'destructive', title: AGENT_OFFLINE_MESSAGE });
      return Promise.reject(error);
    }

    if (error?.response?.status === 400) {
      try {
        const detail = (error.response.data as Record<string, unknown>)?.detail;
        if (typeof detail === 'object' && detail !== null) {
          const detailObj = detail as Record<string, unknown>;
          if (
            'type' in detailObj &&
            detailObj.type === CONTENT_SECURITY_ERROR.PROMPT_INJECTION
          ) {
            const msg = 'msg' in detailObj ? detailObj.msg : undefined;
            const message =
              typeof msg === 'string' && msg
                ? msg
                : CONTENT_SECURITY_ERROR.DEFAULT_MESSAGE;
            return Promise.reject(new Error(message));
          }
        }
      } catch {
        // Malformed response body - fall through to default error handling.
      }
    }

    if (
      error?.response?.status === 403 &&
      isUserRefBannedErrorResponse(error.response.data)
    ) {
      return Promise.reject(
        new Error(TIMELINE_STREAM_ERROR.USER_REF_BANNED, { cause: error })
      );
    }

    // Removed from the active workspace: requests carrying the stale
    // `Heidi-Workspace-Id` header 403 with this detail type. Refetching the
    // workspace list (server-side exempt from the workspace header, so it can
    // never 403 on membership) lets `useHydrateWorkspaceId` reset the
    // persisted id and reload. `cancelRefetch: false` coalesces the 403 burst
    // into one fetch. On a pre-rollout backend the detail is a plain string:
    // `?.type` is undefined and this never matches.
    const errorDetail = (
      error?.response?.data as { detail?: { type?: unknown } } | undefined
    )?.detail;
    if (
      error?.response?.status === 403 &&
      errorDetail?.type === NOT_A_WORKSPACE_MEMBER_TYPE
    ) {
      void reactQueryClient.invalidateQueries(
        { queryKey: [GET_WORKSPACES] },
        { cancelRefetch: false }
      );
      return Promise.reject(error);
    }

    // A suspended account returns 403 with `code: 'ACCOUNT_SUSPENDED'` on every
    // request. Hard-redirect to the terminal suspended page (replace, not push,
    // to cancel in-flight Next.js navigations). The pathname guard prevents a
    // reload loop when the page itself triggers a suspended 403. Public/anonymous
    // `/open-api/` flows are exempt (no account to suspend) — mirroring the 401
    // carve-out below — so they reject normally instead of redirecting.
    if (
      error?.response?.status === 403 &&
      (error.response.data as Record<string, unknown>)?.code === 'ACCOUNT_SUSPENDED' &&
      !error?.response?.config?.baseURL?.includes('/open-api/')
    ) {
      if (
        typeof window !== 'undefined' &&
        window.location.pathname !== '/account-suspended'
      ) {
        window.location.replace('/account-suspended');
      }
      return;
    }

    if (error?.response?.status === 401) {
      if (error?.response?.config?.baseURL?.includes('/open-api/')) {
        return;
      }

      if (error?.config?.url?.includes('/chat/shared/')) {
        return Promise.reject(error);
      }

      // Kinde→Auth0 migration revoke: `revokeKindeSession` posts /user/logout
      // carrying an explicit (old) Kinde token while Redux already holds the
      // new Auth0 token. If the old token is already expired the revoke 401s —
      // that's a no-op (already revoked), never a session expiry, so suppress
      // the forced logout that would otherwise kick the just-migrated user.
      if (error?.config?.explicitKindeToken) {
        return Promise.reject(error);
      }

      // A 401 only says something about the session that authenticated the
      // request. If this request left without a bearer, a token that appeared
      // while it was in flight belongs to a newer session and must not be
      // logged out because of this stale anonymous response.
      if (
        wasRequestDispatchedWithoutBearer(error.config) &&
        !isTokenless401LogoutGuardKilled()
      ) {
        logger.info(
          `${prefix} 401 from request without bearer - rejecting non-destructively`,
          {
            url: error.config?.url,
            method: error.config?.method,
          }
        );
        return Promise.reject(error);
      }

      const token = store.select.auth.getToken(store.getState());
      if (!token) {
        return Promise.reject(error);
      }

      // During an intentional logout, logoutFromServer() revokes the token
      // server-side before dispatch.auth.clear() removes it from Redux.
      // In-flight or refetched requests hitting 401 in this window are
      // expected - suppress the session expired toast and recursive logout.
      const isLogoutInProgress = isLogoutInFlight();
      if (isLogoutInProgress) {
        logger.info(`${prefix} 401 during active logout - suppressing`, {
          url: error.config?.url,
        });
        return Promise.reject(error);
      }

      logger.info(`${prefix} 401 received - triggering session-expiry logout`, {
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        method: error.config?.method,
      });

      debouncedExpiryLogout();
      return;
    } else {
      // `error.message` can embed the request URL and body.
      logger.error(`${prefix} request failed`, sanitizeError(error));
    }

    return Promise.reject(error);
  };
}

/**
 * Tier 2 response handler — attached to `identityBrokerInstance` and
 * `openApiInstance`. Both flows authenticate outside the cache (identityBroker
 * straddles pre-auth determine-login + post-auth assign-apple-migration-ticket;
 * openApi uses the widget token), so a 401 here does NOT represent
 * main-session death and must not trigger `debouncedExpiryLogout`. Header
 * redaction, openApi swallow, and `/chat/shared/` short-circuit are preserved
 * for parity with the legacy handler's 401 carve-outs. Non-401 errors delegate
 * to the legacy handler so EHR 5xx toasts, ACCOUNT_SUSPENDED redirect,
 * prompt-injection 400, user-ref-banned 403, and generic logging all behave
 * exactly as today.
 */
export function createNonCacheAuthHttpRejectedErrorHandler(clientName: string) {
  const prefix = `[${clientName}]`;
  const legacyHandler = createHttpRejectedErrorHandler(clientName);
  return (error: AxiosError) => {
    if (error?.response?.status === 401) {
      redactHeadersInPlace(error.config?.headers);
      redactHeadersInPlace(error.response?.config?.headers);

      if (error?.response?.config?.baseURL?.includes('/open-api/')) {
        return;
      }
      if (error?.config?.url?.includes('/chat/shared/')) {
        return Promise.reject(error);
      }

      logger.info(`${prefix} 401 on non-cache-auth path - propagating without logout`, {
        url: error.config?.url,
        baseURL: error.config?.baseURL,
      });
      return Promise.reject(error);
    }
    return legacyHandler(error);
  };
}

export const handleScribeHttpRejectedErrorNonCacheAuth =
  createNonCacheAuthHttpRejectedErrorHandler('scribeHTTP-non-cache');

// Set on the request config the first time the WWW-Authenticate-gated retry
// path fires. A second 401 with the marker tripped means the BE rejected even
// the freshly-issued token, regardless of cache atom state.
type RetriableConfig = AuthRetriedConfig;

type AxiosRetryFn = (config: InternalAxiosRequestConfig) => Promise<AxiosResponse>;

/**
 * Tier 1 response handler — adds WWW-Authenticate-gated 401-retry-once on
 * top of the legacy handler's pre-401 carve-outs. Attached to the four
 * cache-authed instances (`scribeHttp`, `scribeHttpV2`, `scribeHttpV2_1`,
 * `scribeHttpV2_2`). Tier 2 (openApi) uses
 * `handleScribeHttpRejectedErrorNonCacheAuth` because that flow doesn't
 * authenticate via the cache.
 *
 * Retry semantics:
 *
 * - Only retries when the 401 response carries a `WWW-Authenticate` header
 *   (FastAPI's auth-deps set it automatically; business-logic 401s do not).
 *   Without the header (or without a config to read it from) the 401 is
 *   not treated as a cache-auth failure: it redacts and rejects the
 *   ORIGINAL error directly — no legacy delegation, no retry, no refresh,
 *   no logout, since a header-less 401 is never provably a cache-auth
 *   failure and must not be treated as session death just because a Redux
 *   token happens to exist. NOTE: cross-origin, the browser cannot read
 *   `WWW-Authenticate` until the API exposes it via
 *   `Access-Control-Expose-Headers` (tracked in ml-scribe); until that ships,
 *   every auth 401 is headerless here and rejects non-destructively rather
 *   than retrying. Rollout gate: do not enable the flag before that backend
 *   change is live.
 *
 * - First 401 with the header → set `__authRetryAttempted`, force-refresh
 *   via `getAccessToken({ forceRefresh: true })`, and:
 *     - fresh token returned → retry the original request once on the
 *       same axios instance (the cache-aware request interceptor on that
 *       instance attaches the new Bearer header automatically).
 *     - force-refresh returned null → atom classification:
 *         - `authTransientFailureAtom === true` → suppress logout
 *           (BE 401 may be a transient infra blip; preserve the cached
 *           token, propagate the rejection so the caller can retry).
 *         - `authUnauthenticatedReasonAtom === 'session_invalid'` →
 *           definitive cache classification; mid-action so fire
 *           `debouncedExpiryLogout({ reason: 'request_401' })`. The atom
 *           subscriber owns `session_invalid_query` for idle/passive
 *           transitions; this branch is request-driven.
 *         - both atoms at reset (bare-null / no-token branch per the
 *           four-outcome cache contract) → BE 401 confirms; fire
 *           `debouncedExpiryLogout({ reason: 'request_401' })`.
 *
 * - Second 401 after retry (`__authRetryAttempted === true`) → BE rejected
 *   the freshly-issued token. Definitive regardless of atoms; fire
 *   `debouncedExpiryLogout({ reason: 'request_401' })`.
 *
 * - Account-suspended 403, prompt-injection 400, ehr-agent 503/504,
 *   user-ref-banned 403, wifi-sync network errors — all preserved from
 *   the legacy handler.
 */
export function createScribeHttpRejectedErrorWithRetry(clientName: string) {
  const prefix = `[${clientName}]`;
  const legacyHandler = createHttpRejectedErrorHandler(clientName);

  // One record per terminal decision below, immediately before the reject/
  // retry/logout it explains — see auth401RecoveryTelemetry.ts for why the
  // dimensions are a trailing argument rather than a child() binding.
  const recordRoute = (routeReason: Auth401RecoveryRouteReason, provider: string): void =>
    recordAuth401RecoveryRoute({
      ...routeReason,
      provider,
      platform: isTauri() ? 'desktop' : 'web',
      clientName,
    });

  return async (error: AxiosError, retry: AxiosRetryFn): Promise<unknown> => {
    // A stale-pin cancellation from the request interceptor — reject it
    // directly before anything below reads `.response`/`.config` fields
    // that were never populated. `legacyHandler` below would also catch
    // this (status !== 401), but recognising it explicitly here keeps the
    // non-destructive classification visible at the point it happens
    // rather than relying on a fallthrough.
    const staleRetryRejection = rejectIfAuthRetryStale(error);
    if (staleRetryRejection) return staleRetryRejection;

    // Extract the request's bearer token BEFORE redaction so the serialize
    // path in getAccessTokenWithRevision can tell a fresh in-flight result
    // from the same stale token that triggered this retry.
    const rawAuth = error.config?.headers?.['Authorization'];
    const priorToken =
      typeof rawAuth === 'string' && rawAuth.startsWith('Bearer ')
        ? rawAuth.slice('Bearer '.length)
        : undefined;

    // Unconditional entry redaction — legacy-handler parity. Redact before
    // any branch so no reject path leaks a live Bearer (the three paths that
    // reject without entering the refresh flow — logout-in-progress,
    // explicit_logout, second-401-after-retry — previously escaped this).
    // Non-401 always delegated to the legacy handler, which redacts at its
    // own top. Safe on the retry path because the request interceptor
    // rebuilds `Authorization` from the freshly-issued token before the
    // retry fires; `redactHeadersInPlace` only touches headers the
    // interceptor rebuilds (today: Authorization).
    redactHeadersInPlace(error.config?.headers);
    redactHeadersInPlace(error.response?.config?.headers);

    const response = error.response;
    const status = response?.status;

    // Non-401 responses + 401s on instances where the retry isn't safe
    // (no config, in-progress logout, etc.) fall through to the legacy
    // handler's existing carve-outs and short-circuits — same behaviour
    // as today.
    if (status !== 401) {
      return legacyHandler(error);
    }

    const config = error.config as RetriableConfig | undefined;

    // Defensive: nothing below is readable without a config (no captured
    // context, no url, no retry target) — behave exactly like a
    // non-eligible request rather than inventing a separate code path.
    if (!config) {
      return legacyHandler(error);
    }

    // Kinde→Auth0 migration revoke — explicitKindeToken guard: the request carries the old Kinde
    // token as an explicit override (NOT through getAuthToken). If it
    // 401s, retrying with a freshly force-refreshed Auth0 token would
    // still fail (wrong token class for the Kinde-scoped endpoint) and
    // route to the second-401 branch → forced logout mid-migration.
    // Same semantics as the legacy handler's carve-out; redact the field
    // explicitly here because redactHeadersInPlace only scrubs the Authorization
    // header, not the explicitKindeToken config field. Independent of
    // eligibility/header — explicitKindeToken requests never carry a
    // captured context (see resolveAuthContext), so this must run before
    // the eligibility split rather than rely on falling through it.
    if (config.explicitKindeToken) {
      config.explicitKindeToken = '[REDACTED]';
      return Promise.reject(error);
    }

    // Provider + authRevision the request's bearer was captured under.
    // Never carries the token itself (see the field doc in
    // @heidi/api/client/types). `provider` is always the ACTUAL dispatch
    // provider now — never erased — so eligibility is read from the
    // explicit `eligible` field, never inferred from provider presence.
    //
    // Supersedes the old provider-allowlist gate (mirroring
    // isCacheReadyProvider) that used to sit here: that gate would reject
    // AUTH0_DESKTOP before it ever reached recovery, since AUTH0_DESKTOP is
    // deliberately never added to CACHE_READY_PROVIDERS — recovery
    // eligibility and cache-read readiness are different questions (see
    // isAuth401RecoveryReady / isRecoveryEligible). `eligible` below is the
    // one gate every caller now goes through.
    const capturedContext = config.__authRecoveryContext;

    // A request dispatched without a bearer cannot prove that any session is
    // invalid. In particular, it may return after login has installed a new
    // token; refreshing or logging out that newer session would let an old
    // anonymous response act on an identity it never represented.
    if (wasRequestDispatchedWithoutBearer(config) && !isTokenless401LogoutGuardKilled()) {
      const provider = capturedContext?.provider ?? 'UNKNOWN';
      logger.info(
        `${prefix} 401 from request without bearer - rejecting non-destructively`,
        {
          url: config.url,
          method: config.method,
          provider,
        }
      );
      recordRoute({ route: 'rejected', reason: 'no_bearer' }, provider);
      return Promise.reject(error);
    }

    // Known-stale-at-capture, checked BEFORE the eligibility split. `stale`
    // means the request-context resolver itself detected a mid-resolution
    // identity mismatch (see acceptOrDropContext / the uncertain-identity
    // fallback in getAuthTokenForHttpClient.ts) — the captured
    // {provider, authRevision} pairing already spans two different moments,
    // untrustworthy regardless of provider. Checking this ahead of the
    // eligibility split matters: a non-eligible provider (Kinde, FHIR) with
    // a stale context would otherwise fall into the legacy handler below,
    // which acts unconditionally destructively — exactly the case a stale
    // context must never reach.
    if (capturedContext?.stale) {
      logger.info(
        `${prefix} known-stale captured context - rejecting non-destructively`,
        {
          url: config.url,
          method: config.method,
          provider: capturedContext.provider,
          authRevision: capturedContext.authRevision,
        }
      );
      // Only for a context that WAS recovery-eligible — a stale non-eligible
      // context falls through to legacy handling below, already excluded.
      if (capturedContext.eligible) {
        recordRoute(
          { route: 'rejected', reason: 'context_stale' },
          capturedContext.provider
        );
      }
      return Promise.reject(error);
    }

    // Eligibility split — a non-eligible provider (Kinde, Keycloak,
    // FHIR, or no captured context at all) gets its established legacy
    // behavior, unchanged, regardless of whether this 401 carries a
    // WWW-Authenticate header. Putting the header check ahead of this split
    // (as this used to) silently ate Kinde/Keycloak's legacy logout on any
    // header-less 401 (cross-origin, or a business-logic 401) — the header
    // says nothing about whether the request was ever recovery-eligible.
    // Falsifier: swap this check back below the header check and the
    // eligible-Kinde-without-header test fails.
    if (!capturedContext || !capturedContext.eligible) {
      // Token-free — makes a resulting legacy logout attributable to "not
      // eligible" rather than reading as if this handler was never reached.
      logger.info(`${prefix} not recovery-eligible - delegating to legacy handler`, {
        provider: capturedContext?.provider ?? null,
        platform: isTauri() ? 'desktop' : 'web',
      });
      return legacyHandler(error);
    }

    // From here, capturedContext.eligible is true: this request WAS
    // recovery-eligible at dispatch. Three independent, non-destructive
    // reject reasons before any retry/logout path — none of them delegate
    // to the legacy handler, because legacy-delegating here would act
    // destructively against a session this handler can't yet confirm is
    // the live one.

    // WWW-Authenticate header gate. FastAPI's `Security` / `Depends`
    // chain sets it automatically on auth-rejection paths; manual
    // `raise HTTPException(401)` for permission / scope / business-logic
    // reasons does NOT. Skipping retry when the header is absent is the
    // belt-and-braces backstop against non-auth 401s the ml-scribe audit
    // missed (or hasn't reached yet). Both spellings are pinned — HTTP
    // headers are case-insensitive and proxies / frameworks vary. This
    // intentionally runs BEFORE the __authRetryAttempted check: a genuine
    // auth-retry 401 carries the header; a header-less 401 is never an auth
    // 401, so checking the marker first would force-logout on a non-auth 401
    // that happens after a valid retry.
    const hasWwwAuthenticate =
      response?.headers?.['www-authenticate'] !== undefined ||
      response?.headers?.['WWW-Authenticate'] !== undefined;
    if (!hasWwwAuthenticate) {
      logger.info(
        `${prefix} eligible 401 without WWW-Authenticate - rejecting non-destructively`,
        {
          url: config.url,
          method: config.method,
          provider: capturedContext.provider,
        }
      );
      recordRoute(
        { route: 'rejected', reason: 'header_missing' },
        capturedContext.provider
      );
      return Promise.reject(error);
    }

    // `capturedContext.stale` was already checked (and would have
    // rejected) BEFORE the eligibility split above, ahead of this point.

    // Confirm the captured context is STILL current. A mismatch means the
    // session has already been replaced (e.g. account A's retried request
    // in flight while account B logs in); neither legacy-delegating
    // (destructive against the NEW session) nor proceeding with a stale
    // context is safe, so reject the original error non-destructively.
    if (!(await isCapturedContextCurrent(capturedContext))) {
      logger.info(
        `${prefix} captured context no longer current - rejecting non-destructively`,
        {
          url: config.url,
          method: config.method,
          provider: capturedContext.provider,
          authRevision: capturedContext.authRevision,
        }
      );
      recordRoute(
        { route: 'rejected', reason: 'context_stale' },
        capturedContext.provider
      );
      return Promise.reject(error);
    }

    // isRecoveryEligible re-reads the desktop rollout flag live, so this is
    // the flag-flip mid-flight recheck — a request stamped eligible at
    // dispatch whose flag has since flipped off falls through here.
    // Delegate to the legacy handler, byte-equivalent to the non-eligible path.
    if (!isRecoveryEligible(capturedContext.provider as AuthProvider, isTauri())) {
      recordRoute({ route: 'legacy', reason: 'flag_disabled' }, capturedContext.provider);
      return legacyHandler(error);
    }

    // Lazy import — same static-cycle constraint as the @/lib/auth import.
    const { authCacheSuppressedAtom } = await import('@heidi/auth/state');
    const jotaiStore = getDefaultStore();

    // Suppression-at-entry. The request-path context resolver already
    // skips recovery inside a suppressed context; this mirrors that gate
    // in the handler itself so a 401 landing here mid-suppression (the
    // embed race) can't dispatch a force-refresh at all.
    if (jotaiStore.get(authCacheSuppressedAtom)) {
      logger.info(`${prefix} 401 during suppressed auth context - suppressing retry`, {
        url: config.url,
      });
      recordRoute({ route: 'rejected', reason: 'suppressed' }, capturedContext.provider);
      return Promise.reject(error);
    }

    // In-progress intentional logout — suppress retry, suppress logout.
    const isLogoutInProgress = isLogoutInFlight();
    if (isLogoutInProgress) {
      logger.info(`${prefix} 401 during active logout - suppressing retry`, {
        url: config.url,
      });
      recordRoute(
        { route: 'rejected', reason: 'session_transition' },
        capturedContext.provider
      );
      return Promise.reject(error);
    }

    // Import atoms early so we can check the explicit_logout state
    // BEFORE awaiting the force-refresh. A logout that started and
    // resolved between the isLogoutInProgress check and the
    // getAccessTokenWithRevision await would otherwise sneak through.
    const {
      authTransientFailureAtom,
      authUnauthenticatedReasonAtom,
      getAccessTokenWithRevision,
    } = await import('@/lib/auth');

    const preRefreshReason = jotaiStore.get(authUnauthenticatedReasonAtom);
    if (preRefreshReason === 'explicit_logout') {
      logger.info(`${prefix} 401 post explicit-logout - suppressing retry`, {
        url: config.url,
      });
      recordRoute(
        { route: 'rejected', reason: 'session_transition' },
        capturedContext.provider
      );
      return Promise.reject(error);
    }

    // Second 401 after retry — BE rejected the freshly-issued token.
    // Definitive regardless of cache atom state because the BE's
    // rejection of a token the cache just minted is the signal itself.
    // Still fenced: the retry's OWN interceptor pass re-stamped
    // capturedContext at retry-dispatch time, but real time has passed
    // since (the round trip) — a session replacement in that window means
    // this "definitive" signal is about an already-superseded session.
    if (config.__authRetryAttempted === true) {
      if (!(await isCapturedContextCurrent(capturedContext))) {
        logger.info(`${prefix} second 401 but captured context stale - no logout`, {
          url: config.url,
        });
        recordRoute(
          { route: 'rejected', reason: 'context_stale' },
          capturedContext.provider
        );
        return Promise.reject(error);
      }
      logger.info(
        `${prefix} retried 401 also failed → forced logout (reason: request_401)`,
        {
          url: config.url,
        }
      );
      recordRoute({ route: 'logout', reason: 'second_401' }, capturedContext.provider);
      fireRequest401Logout();
      return Promise.reject(error);
    }

    // First 401 with WWW-Authenticate. Force-refresh through the cache.
    // The cache's launchFetch never throws (collapses to null at the
    // public API); atom side-effects classify the outcome.
    // (Headers already redacted at handler entry above.)
    logger.info(`${prefix} 401 received - force-refreshing then retrying once`, {
      url: config.url,
      method: config.method,
    });

    const outcome = await getAccessTokenWithRevision({
      forceRefresh: true,
      priorToken,
      // Narrowed to exactly what GetAccessTokenOptions.expected declares —
      // capturedContext also carries eligible/stale, which the cache has no
      // business seeing.
      expected: {
        provider: capturedContext.provider,
        authRevision: capturedContext.authRevision,
      },
    });

    // Mid-flight suppression recheck — suppression can flip on during the
    // await, independent of the entry check above.
    if (jotaiStore.get(authCacheSuppressedAtom)) {
      logger.info(
        `${prefix} suppression started during force-refresh - suppressing retry`,
        {
          url: config.url,
        }
      );
      recordRoute({ route: 'rejected', reason: 'suppressed' }, capturedContext.provider);
      return Promise.reject(error);
    }

    if (outcome.status === 'stale') {
      // The captured context no longer matches live state — something
      // else already changed the session since this request's bearer was
      // captured. Abandon non-destructively; the next request captures a
      // fresh context and, if the session is genuinely dead, will surface
      // that on its own terms.
      logger.info(`${prefix} 401 → captured context stale, abandoning retry`, {
        url: config.url,
      });
      recordRoute(
        { route: 'rejected', reason: 'context_stale' },
        capturedContext.provider
      );
      return Promise.reject(error);
    }

    if (outcome.status === 'success') {
      // Re-check logout after the awaits. A logout can start (and even
      // resolve) while the force-refresh was in-flight. If it did,
      // dispatching retry() here would send a fresh token into a
      // post-logout request — potentially writing it back into Redux
      // and localStorage via mirror-write while the auth cookie /
      // server session are already invalidated.
      if (isLogoutInFlight()) {
        logger.info(`${prefix} logout started during force-refresh - suppressing retry`, {
          url: config.url,
        });
        recordRoute(
          { route: 'rejected', reason: 'session_transition' },
          capturedContext.provider
        );
        return Promise.reject(error);
      }

      // Retry on the same instance — cache-aware request interceptor
      // attaches the fresh Bearer header. If the retry itself returns
      // 401, this handler runs again with __authRetryAttempted === true
      // and routes to the second-401 branch above.
      //
      // Stamp both markers only here, immediately before dispatching the
      // retry. Setting them earlier (before the force-refresh) would leave
      // them on the config after the non-success branches reject, where
      // __authRetryAttempted could trip the "second 401 → forced logout"
      // check on any later retry of the same config (axios-retry queues,
      // manual product-code retries, etc.). With both markers scoped to
      // the retry-dispatch moment, only THIS retry's response can see
      // them. __expectedAuthIdentity pins the retry's own request
      // interceptor to this exact commit — see resolveAuthContext.
      // `provider` comes from `capturedContext` (a provider change mid
      // force-refresh would already have classified the outcome 'stale',
      // never reaching this branch); `authRevision` is the fresh
      // POST-commit value the cache just published.
      config.__authRetryAttempted = true;
      config.__expectedAuthIdentity = {
        provider: capturedContext.provider,
        authRevision: outcome.authRevision,
      };
      recordRoute(
        { route: 'retry_requested', reason: 'fresh_token' },
        capturedContext.provider
      );
      return retry(config);
    }

    // outcome.status === 'no-token'. Classify on atoms.
    const isTransient = jotaiStore.get(authTransientFailureAtom);
    const reason = jotaiStore.get(authUnauthenticatedReasonAtom);

    if (isTransient) {
      logger.info(`${prefix} 401 → force-refresh transient, suppressing logout`, {
        url: config.url,
      });
      recordRoute(
        { route: 'rejected', reason: 'refresh_transient' },
        capturedContext.provider
      );
      return Promise.reject(error);
    }

    if (reason === 'explicit_logout') {
      // Logout completed during the force-refresh await. Suppress the redundant
      // logout — firing debouncedExpiryLogout or deleteAuthProviderCookiesBeforeLogout
      // would race the ongoing logout redirect and double telemetry/cookie-clears.
      // The intentional logout owns its own teardown.
      logger.info(
        `${prefix} 401 → logout completed during force-refresh, suppressing redundant logout`,
        { url: config.url }
      );
      recordRoute(
        { route: 'rejected', reason: 'session_transition' },
        capturedContext.provider
      );
      return Promise.reject(error);
    }

    // Both remaining branches are destructive (logout) — fence against a
    // session already replaced during the force-refresh await, same as the
    // second-401 branch above. The cache's own classification (session_invalid
    // / bare-null) is about the token it just tried; it says nothing about
    // whether THIS request's captured session is still the live one.
    if (!(await isCapturedContextCurrent(capturedContext))) {
      logger.info(
        `${prefix} 401 classified for logout but captured context stale - no logout`,
        {
          url: config.url,
        }
      );
      recordRoute(
        { route: 'rejected', reason: 'context_stale' },
        capturedContext.provider
      );
      return Promise.reject(error);
    }

    if (reason === 'session_invalid') {
      logger.info(`${prefix} 401 → cache classified session_invalid → forced logout`, {
        url: config.url,
      });
      // `request_401` (not `session_invalid_query`): this branch fires from
      // the axios path which is by definition mid-action. `session_invalid_query`
      // belongs to the atom subscriber (`useSessionInvalidationTelemetry`),
      // which surfaces idle/passive cache invalidations independent of any
      // user request. Splitting the reasons keeps mid-action logout
      // distinguishable from idle session-death in telemetry.
      recordRoute(
        { route: 'logout', reason: 'session_invalid' },
        capturedContext.provider
      );
      fireRequest401Logout();
      return Promise.reject(error);
    }

    // Bare-null / no-token branch — cache returned null without a
    // definitive signal AND no transient flag. The BE's 401 is itself
    // the signal that the session is dead; complete the logout flow.
    logger.info(
      `${prefix} 401 → bare-null cache outcome, BE rejection is the signal → forced logout`,
      {
        url: config.url,
      }
    );
    recordRoute({ route: 'logout', reason: 'no_token' }, capturedContext.provider);
    fireRequest401Logout();
    return Promise.reject(error);
  };
}

// Distinct clientName from the legacy `'scribeHTTP'` so the [scribeHTTP-retry]
// log prefix lets dashboards/grep distinguish the new retry path from the
// pre-existing handler — both run against the same four cache-authed
// instances depending on the flag.
export const handleScribeHttpRejectedErrorWithRetry =
  createScribeHttpRejectedErrorWithRetry('scribeHTTP-retry');
